/**
 * useTurnstile.ts
 * Cloudflare Turnstile ウィジェットの明示的レンダリング用フック
 * - フォームが表示されたタイミングでウィジェットを描画
 * - チャレンジ完了後にトークンを返す
 * - コンポーネントのアンマウント時にウィジェットを破棄
 */
import { useEffect, useRef, useState, useCallback } from "react";

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: string | HTMLElement,
        options: {
          sitekey: string;
          theme?: "light" | "dark" | "auto";
          size?: "normal" | "compact" | "flexible";
          callback?: (token: string) => void;
          "error-callback"?: (errorCode: string) => void;
          "expired-callback"?: () => void;
          "timeout-callback"?: () => void;
          action?: string;
        }
      ) => string;
      remove: (widgetId: string) => void;
      reset: (widgetId: string) => void;
      getResponse: (widgetId: string) => string | undefined;
    };
  }
}

interface UseTurnstileOptions {
  /** Cloudflare Turnstile サイトキー */
  siteKey: string;
  /** ウィジェットのテーマ（デフォルト: dark） */
  theme?: "light" | "dark" | "auto";
  /** アクション識別子 */
  action?: string;
}

interface UseTurnstileReturn {
  /** ウィジェットをマウントするコンテナへの ref */
  containerRef: React.RefObject<HTMLDivElement | null>;
  /** チャレンジ完了後のトークン（未完了時は null） */
  token: string | null;
  /** エラーメッセージ */
  error: string | null;
  /** ウィジェットをリセットする */
  reset: () => void;
}

export function useTurnstile({
  siteKey,
  theme = "dark",
  action = "submit_comment",
}: UseTurnstileOptions): UseTurnstileReturn {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const renderWidget = useCallback(() => {
    if (!containerRef.current || !window.turnstile) return;

    // 既存ウィジェットを破棄してから再描画
    if (widgetIdRef.current) {
      try {
        window.turnstile.remove(widgetIdRef.current);
      } catch (_) {
        // 無視
      }
      widgetIdRef.current = null;
    }

    setToken(null);
    setError(null);

    widgetIdRef.current = window.turnstile.render(containerRef.current, {
      sitekey: siteKey,
      theme,
      action,
      callback: (t: string) => {
        setToken(t);
        setError(null);
      },
      "error-callback": (code: string) => {
        setError(`Turnstile エラー: ${code}`);
        setToken(null);
      },
      "expired-callback": () => {
        setToken(null);
        setError("チャレンジの有効期限が切れました。もう一度お試しください。");
      },
      "timeout-callback": () => {
        setToken(null);
        setError("チャレンジがタイムアウトしました。もう一度お試しください。");
      },
    });
  }, [siteKey, theme, action]);

  useEffect(() => {
    // Turnstile スクリプトがまだロードされていない場合はポーリングで待機
    let attempts = 0;
    const maxAttempts = 20;
    const interval = setInterval(() => {
      attempts++;
      if (window.turnstile) {
        clearInterval(interval);
        renderWidget();
      } else if (attempts >= maxAttempts) {
        clearInterval(interval);
        setError("Turnstile の読み込みに失敗しました。ページを再読み込みしてください。");
      }
    }, 300);

    return () => {
      clearInterval(interval);
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch (_) {
          // 無視
        }
      }
    };
  }, [renderWidget]);

  const reset = useCallback(() => {
    if (widgetIdRef.current && window.turnstile) {
      window.turnstile.reset(widgetIdRef.current);
      setToken(null);
      setError(null);
    }
  }, []);

  return { containerRef, token, error, reset };
}
